import RpcService from '@/core/services/Rpc/RpcService';
import RpcResponse from '@/core/services/Rpc/RpcResponse';

import type Receipt           from '@/domains/Receipts/Receipt';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import type DateRange         from '@/core/types/DateRange';

/**
 * DAO для работы с приемками
 */
class ReceiptsDAO {

	/**
	 * Загружает одну приемку по ID (с позициями и расхождениями)
	 */
	static async get(id: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('receipts.get', { id });
	}

	/**
	 * Загружает список приемок
	 */
	static async getList(
		filters?: {
			id?: number;
			shipmentIdPattern?: string;
			orderPositionIdPattern?: string;
			period?: DateRange;
			hasComplaints?: boolean;
			sellerId?: number;
      sellerCompanyId?: number;
			buyerId?: number;
      buyerCompanyId?: number;
			requireNestedObjects?: boolean;
      isClosed?: boolean;
		},
		pagination?: RpcDataPagination,
		sort?: RpcDataSort,
	): Promise<RpcResponse> {
		return await RpcService.makeRequest(
			'receipts.getList',
			filters,
			pagination,
			sort,
			true, null, null, false,
			{ abortPrevious: true },
		);
	}

	/**
	 * Создаёт или обновляет приемку.
	 * Бэк определяет add/update по наличию receipt.id.
	 */
	static async upsert(receipt: Receipt): Promise<RpcResponse> {
		return await RpcService.makeRequest('receipts.upsert', { receipt });
	}

	/**
	 * Удаляет приемку.
	 * Бэку нужен только id, поэтому из id собираем минимальный объект {receipt: {id}}.
	 */
	static async delete(id: number, changeReasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('receipts.delete', {
			receipt: { id },
			changeReasonText,
		});
	}

  /**
   * Отправляет уведомление о ручной приемке.
   * Бэку нужен только id приемки, поэтому собираем минимальный объект {receipt: {id}}.
   * Получателей бэк сверяет со своим списком и отклоняет запрос, если среди них есть посторонние.
   */
  static async sendNotification(id: number, receivers: { id: number }[]): Promise<RpcResponse> {
    return await RpcService.makeRequest('receipts.sendNotification', {
      receipt: { id },
      receivers,
    });
  }
}

export default ReceiptsDAO;
